/**
 * Verlauf-Tab (#130): Notizen aus `project_history.note` lesbar machen.
 *
 * Mehrere Ereignisse speichern JSON (Sync-Historie, führender Auftrag, Zusammenführen). Roh
 * angezeigt war das unlesbar. Hier entsteht daraus ein Satz („Bereich: PU → Service (P022001)");
 * Klartext-Notizen bleiben unverändert. Das Format der Sync-Ereignisse teilen sich Schreiber
 * (API, `sync/zuordnungs-historie.ts`) und Leser über die Schemas in `@projektverwaltung/shared`.
 */
import {
  BedarfZugeordnetNotizSchema,
  BesetzungNotizSchema,
  type SfImportiertNotiz,
  SfImportiertNotizSchema,
  SfZuordnungGeaendertNotizSchema,
  type ZuordnungsFeld,
} from '@projektverwaltung/shared';

export type VerlaufT = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export type VerlaufNotiz = {
  /** Abweichendes Label für das Ereignis (z. B. führender Auftrag gewechselt). */
  label?: string;
  text: string | null;
};

const LEER = '—';

function feldLabel(feld: ZuordnungsFeld, t: VerlaufT): string {
  switch (feld) {
    case 'segment':
      return t('v2.detail.verlauf_feld_segment', 'Segment');
    case 'bereich':
      return t('v2.detail.verlauf_feld_bereich', 'Bereich');
    case 'dienstleistungsart':
      return t('v2.detail.verlauf_feld_dienstleistungsart', 'Dienstleistungsart');
    case 'projektleitung':
      return t('v2.detail.verlauf_feld_projektleitung', 'Projektleitung');
    case 'manager':
      return t('v2.detail.verlauf_feld_manager', 'Manager');
  }
}

function alsObjekt(note: string): Record<string, unknown> | null {
  if (!note.startsWith('{')) return null;
  try {
    const v: unknown = JSON.parse(note);
    return v !== null && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

const BESETZUNG_EVENTS = new Set([
  'besetzung_angelegt',
  'besetzung_status_geaendert',
  'besetzung_zugeordnet',
  'besetzung_verknuepft',
  'besetzung_geloescht',
]);

const STATUS_WERTE = new Set(['offen', 'vorgeschlagen', 'float', 'erledigt', 'ausgeplant']);

function statusWort(wert: string, t: VerlaufT): string {
  return STATUS_WERTE.has(wert) ? t(`v2.staffing_maske.status.${wert}`, wert) : wert;
}

function besetzungFeldLabel(feld: 'position' | 'bedarf' | 'sf', t: VerlaufT): string {
  switch (feld) {
    case 'position':
      return t('v2.detail.verlauf_feld_position', 'Position');
    case 'bedarf':
      return t('v2.detail.verlauf_feld_bedarf', 'Bedarf');
    case 'sf':
      return t('v2.detail.verlauf_feld_sf', 'Salesforce');
  }
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function verlaufNotiz(
  eventType: string,
  note: string | null | undefined,
  t: VerlaufT,
): VerlaufNotiz {
  if (!note) return { text: null };
  const obj = alsObjekt(note);
  if (!obj) return { text: note };

  if (eventType === 'sf_zuordnung_geaendert') {
    const d = SfZuordnungGeaendertNotizSchema.safeParse(obj);
    if (!d.success) return { text: note };
    return {
      text: `${feldLabel(d.data.feld, t)}: ${d.data.alt ?? LEER} → ${d.data.neu ?? LEER} (${d.data.project_key})`,
    };
  }

  if (BESETZUNG_EVENTS.has(eventType)) {
    const d = BesetzungNotizSchema.safeParse(obj);
    if (!d.success) return { text: note };
    const { person, feld, alt, neu, automatisch } = d.data;
    const istStatus =
      feld === 'status' || (feld === undefined && eventType.endsWith('status_geaendert'));
    const wert = (v: string | null) => (v === null ? LEER : istStatus ? statusWort(v, t) : v);
    const kopf = feld && feld !== 'status' ? `${person} (${besetzungFeldLabel(feld, t)})` : person;
    let text = alt === null && neu === null ? kopf : `${kopf}: ${wert(alt)} → ${wert(neu)}`;
    if (automatisch) {
      text += ` ${t('v2.detail.verlauf_automatisch', '(automatisch übernommen)')}`;
    }
    return { text };
  }

  if (eventType === 'bedarf_zugeordnet') {
    const d = BedarfZugeordnetNotizSchema.safeParse(obj);
    if (!d.success) return { text: note };
    const nicht = t('v2.detail.verlauf_nicht_zugeordnet', 'nicht zugeordnet');
    return { text: `${d.data.rolle}: ${d.data.alt ?? nicht} → ${d.data.neu ?? nicht}` };
  }

  if (eventType === 'sf_importiert') {
    const d = SfImportiertNotizSchema.safeParse(obj);
    if (!d.success) return { text: note };
    const werte: Array<[ZuordnungsFeld, keyof SfImportiertNotiz]> = [
      ['segment', 'segment'],
      ['bereich', 'bereich'],
      ['dienstleistungsart', 'dienstleistungsart'],
      ['projektleitung', 'projektleitung'],
      ['manager', 'manager'],
    ];
    const teile = werte.map(([f, k]) => `${feldLabel(f, t)}: ${d.data[k] ?? LEER}`);
    return { text: [d.data.project_key, ...teile].join(' · ') };
  }

  // Historisch heißt das Ereignis `responsibility_assigned`; gemeint ist der Wechsel des
  // führenden Auftrags (services/fuehrender-auftrag.ts).
  if (obj.kind === 'leading_auftrag_changed') {
    const von = str(obj.from_project_key);
    const nach = str(obj.new_project_key) ?? LEER;
    const anlass = str(obj.anlass);
    return {
      label: t('v2.detail.audit_event_leading_auftrag_changed', 'Führender Auftrag gewechselt'),
      text: `${von ? `${von} ` : ''}→ ${nach}${anlass ? ` · ${anlass}` : ''}`,
    };
  }

  // Übrige JSON-Notizen (Herauslösen, Zusammenführen, Wiederherstellen): P-Nummern statt Ids.
  const key = str(obj.project_key);
  if (key) {
    const ziel = str(obj.to_project_key);
    return { text: ziel ? `${key} → ${ziel}` : key };
  }
  return { text: note };
}
